import { IResponseLogin } from '@apiinterface/IAuth'
import api from '@config/api'
import { notifications } from '@mantine/notifications'
import { IconExclamationCircle } from '@tabler/icons-react'
import React, { createContext, useEffect, useState } from 'react'
import { useCookies } from 'react-cookie'

interface IAuthContext {
    auth: IResponseLogin | null
    login: (data: IResponseLogin) => void
    logout: () => void
    isAuthenticated: boolean
    isLoading: boolean
    checkRole: (role?: number[]) => boolean
}

export const AuthContext = createContext<IAuthContext>({
    auth: null,
    login: () => {},
    logout: () => {},
    isAuthenticated: false,
    isLoading: false,
    checkRole: () => false,
})

const AuthProvider = ({ children }: { children: React.ReactNode }) => {
    const [auth, setAuth] = useState<IResponseLogin | null>(null)
    const [cookies, setCookie, removeCookie] = useCookies(['token'])
    const [isLoading, setIsLoading] = useState(true)
    const [isAuthenticated, setIsAuthenticated] = useState(false)
    const login = (data: IResponseLogin): void => {
        removeCookie('token')
        setCookie('token', data.token, {
            path: '/',
            secure: true,
            //httpOnly: true,
            sameSite: 'strict',
        })
        setAuth(data)
        setIsAuthenticated(true)
    }
    const query = async () => {
        try {
            const res = await api.post('/auth/refreshtoken')
            removeCookie('token')
            const data = res.data.body as IResponseLogin
            setIsAuthenticated(true)
            setCookie('token', data.token, {
                path: '/',
                secure: true,
                //httpOnly: true,
                sameSite: 'strict',
            })
            setAuth(data)
        } catch (err: any) {
            notifications.show({
                title: 'เกิดข้อผิดพลาด',
                autoClose: 5000,
                message: 'กรุณาเข้าสู่ระบบใหม่',
                color: 'red',
                icon: <IconExclamationCircle size='1rem' />,
            })
            removeCookie('token')
        }
        setIsLoading(false)
    }
    useEffect(() => {
        api.defaults.headers.common['Authorization'] = `Bearer ${cookies.token}`
        if (isLoading === true) {
            if (cookies.token) {
                query()
            } else {
                setIsLoading(false)
            }
        }
    }, [cookies.token]) // eslint-disable-line react-hooks/exhaustive-deps

    // Simulate a logout action
    const logout = () => {
        removeCookie('token', { path: '/' })

        setAuth(null)
        setIsAuthenticated(false)
    }

    const checkRole = (role?: number[]): boolean => {
        if (role === undefined) return true
        return auth?.role_id && role?.includes(auth?.role_id) ? true : false
    }

    return (
        <AuthContext.Provider
            value={{
                auth,
                login,
                isLoading,
                logout,
                isAuthenticated,
                checkRole,
            }}
        >
            {children}
        </AuthContext.Provider>
    )
}

export default AuthProvider
